import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { Eye, Plus, Search, Filter, Undo2, Edit, Trash2, FileText, Printer, RefreshCw } from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import PrintModal from '../../components/UI/PrintModal';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { DeliveryInvoicePDF } from './DeliveryInvoicePDF';
import { PurchaseInvoicePDF } from './PurchaseInvoicePDF';
import { useSettings } from '../../contexts/SettingsContext';

interface DeliveryInvoice {
  _id: string;
  invoiceNumber: string;
  customer: { name: string };
  items: Array<{
    product?: { _id: string; name: string; unit?: string };
    name?: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    totalPrice: number;
    discount?: number;
    returnedQty?: number;
  }>;
  createdAt: string;
  project?: { name: string };
  workOrder?: { number?: string; date?: string };
}

interface ReturnInvoice {
  _id: string;
  invoiceNumber?: string;
  returnNumber?: string;
  deliveryInvoiceId: string | DeliveryInvoice;
  customer: { name: string };
  items: Array<{
    productId: string;
    name: string;
    quantity: number;
    unit: string;
    remarks: string;
  }>;
  createdAt: string;
}

interface Project {
  _id: string;
  name: string;
}

const RETURN_CAUSES = [
  'Damaged',
  'Wrong Item',
  'Excess Quantity',
  'Other',
];

const ReturnInvoicesPage: React.FC = () => {
  const [deliveryInvoices, setDeliveryInvoices] = useState<DeliveryInvoice[]>([]);
  const [returnInvoices, setReturnInvoices] = useState<ReturnInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<DeliveryInvoice | null>(null);
  const [returnProducts, setReturnProducts] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState<ReturnInvoice | null>(null);
  const { user } = useAuth();
  const { settings } = useSettings();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchAll();
    fetchProjects();
  }, []);

  // Handle deliveryInvoiceId parameter to auto-open return invoice view
  useEffect(() => {
    const deliveryInvoiceId = searchParams.get('deliveryInvoiceId');
    if (deliveryInvoiceId && returnInvoices.length > 0) {
      // Find return invoices for this delivery invoice
      const relatedReturns = returnInvoices.filter(returnInv => {
        if (typeof returnInv.deliveryInvoiceId === 'string') {
          return returnInv.deliveryInvoiceId === deliveryInvoiceId;
        } else if (typeof returnInv.deliveryInvoiceId === 'object' && returnInv.deliveryInvoiceId !== null) {
          return returnInv.deliveryInvoiceId._id === deliveryInvoiceId;
        }
        return false;
      });
      
      // If there are return invoices, open the view modal for the first one
      if (relatedReturns.length > 0) {
        setActiveInvoice(relatedReturns[0]);
        setViewModalOpen(true);
      }
    }
  }, [searchParams, returnInvoices]);

  // Soft refresh: refetch returns and delivery invoices when header clears cache
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateInvoicesCache();
      fetchAll();
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [deliveryRes, returnRes] = await Promise.all([
        apiService.get('/invoices?type=delivery'),
        apiService.get('/invoices?type=return'),
      ]);
      setDeliveryInvoices(deliveryRes.data.invoices || []);
      setReturnInvoices(returnRes.data.invoices || []);
    } catch (err) {
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await apiService.get('/projects');
      setProjects(res.data.projects || []);
    } catch (err) {
      toast.error('Failed to fetch projects');
    }
  };

  const openReturnModal = async (invoice: DeliveryInvoice) => {
    setSelectedInvoice(invoice);
    
    // Fetch all return invoices for this delivery invoice to calculate total returned quantities
    try {
      const returnInvoicesResponse = await apiService.get(`/invoices?type=return&deliveryInvoiceId=${invoice._id}`);
      const returnInvoices = returnInvoicesResponse.data.invoices || [];
      
      // Calculate total returned quantity for each product
      const totalReturnedQuantities: { [productId: string]: number } = {};
      
      returnInvoices.forEach((returnInvoice: any) => {
        returnInvoice.items.forEach((item: any) => {
          const productId = item.productId || item.product?._id || item.product;
          if (productId) {
            const qty = item.quantity || 0;
            totalReturnedQuantities[productId] = (totalReturnedQuantities[productId] || 0) + qty;
          }
        });
      });
      
      setReturnProducts(
        (invoice.items || []).map(item => {
          const productId = item.product?._id || '';
          const totalReturned = totalReturnedQuantities[productId] || 0;
          const maxQty = Math.max(0, item.quantity - totalReturned);
          
          return {
            productId,
            name: item.name || item.product?.name || '',
            unit: item.unit || item.product?.unit || '',
            maxQty,
            qty: 0,
            cause: '',
          };
        })
      );
    } catch (error) {
      console.error('Error fetching return invoices:', error);
      // Fallback to using returnedQty from delivery invoice if API call fails
    setReturnProducts(
      (invoice.items || []).map(item => ({
        productId: item.product?._id || '',
        name: item.name || item.product?.name || '',
        unit: item.unit || item.product?.unit || '',
        maxQty: item.quantity - (item.returnedQty || 0),
        qty: 0,
        cause: '',
      }))
    );
    }
    
    setShowReturnModal(true);
  };

  const handleReturnProductChange = (idx: number, field: string, value: any) => {
    setReturnProducts(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const handleSubmitReturn = async () => {
    if (!selectedInvoice) return;
    if (!user?._id) {
      toast.error('User not authenticated');
      return;
    }
    const items = returnProducts.filter(p => p.qty > 0 && p.cause);
    if (items.length === 0) {
      toast.error('Select at least one product and specify cause');
      return;
    }
    setSubmitting(true);
    try {
      // Build items array as required by backend
      const backendItems = items.map(p => ({
        product: p.productId,
        quantity: p.qty,
        unit: p.unit,
        unitPrice: 0, // For returns, price is 0
        totalPrice: 0, // For returns, total is 0
        name: p.name,
        remarks: p.cause // Store return cause in remarks
      }));
      // Calculate subtotal, tax, discount, total (all 0 for returns)
      const subtotal = 0;
      const tax = 0;
      const discount = 0;
      const total = 0;
      await apiService.post('/invoices', {
        type: 'return',
        customer: { name: selectedInvoice.customer?.name || '' },
        items: backendItems,
        status: 'processing',
        subtotal,
        tax,
        discount,
        total,
        preparedBy: user._id,
        notes: `Return for delivery invoice: ${selectedInvoice.invoiceNumber}`,
        deliveryInvoiceId: selectedInvoice._id // <-- send deliveryInvoiceId
      });
      toast.success('Return invoice created');
      setShowReturnModal(false);
      fetchAll();
    } catch (err) {
      toast.error('Failed to create return invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredDeliveryInvoices = deliveryInvoices.filter(inv =>
    !searchTerm ||
    inv.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper to get delivery invoice by ID
  const getDeliveryInvoice = (id: string) => deliveryInvoices.find(d => d._id === id);

  // Action handlers
  const handleView = (invoice: ReturnInvoice) => {
    setActiveInvoice(invoice);
    setViewModalOpen(true);
  };
  const handleEdit = (invoice: ReturnInvoice) => {
    setActiveInvoice(invoice);
    setEditModalOpen(true);
  };
  const handlePrint = (invoice: ReturnInvoice) => {
    setActiveInvoice(invoice);
    setPrintModalOpen(true);
  };
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this return invoice?')) {
      try {
        await apiService.delete(`/invoices/${id}`);
        toast.success('Return invoice deleted');
        fetchAll();
      } catch (err) {
        toast.error('Failed to delete return invoice');
      }
    }
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-4">Return Invoices</h1>
      {loading ? <LoadingSpinner size="lg" /> : (
        <>
          {/* Delivery Invoices List */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <h2 className="text-lg font-semibold flex-1">Delivery Invoices</h2>
              <button
                onClick={() => { apiService.invalidateInvoicesCache(); fetchAll(); }}
                title="Refresh"
                className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <div className="relative max-w-xs flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by invoice or customer..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <DoubleScrollbar className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow">
              <table className="min-w-full divide-y divide-[var(--color-border)]">
                <thead className="bg-[var(--color-background)]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)] text-[var(--color-text)]">
                  {filteredDeliveryInvoices.length === 0 && (
                    <tr><td colSpan={4} className="text-center py-8 text-[var(--color-text-secondary)]">No delivery invoices found.</td></tr>
                  )}
                  {filteredDeliveryInvoices.map(inv => (
                    <tr key={inv._id} className="hover:bg-[var(--color-background-hover)] transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">{inv.invoiceNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{inv.customer?.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{new Date(inv.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          className="inline-flex items-center px-3 py-1.5 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
                          onClick={() => openReturnModal(inv)}
                        >
                          <Undo2 className="w-4 h-4 mr-1" /> Return Products
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DoubleScrollbar>
          </div>

          {/* Return Modal */}
          <Modal isOpen={showReturnModal} onClose={() => setShowReturnModal(false)} title="Return Products from Delivery" size="xl" className="!max-w-7xl">
            {selectedInvoice && (
              <div className="space-y-4 text-[var(--color-text)]">
                <div className="font-semibold">Invoice: {selectedInvoice.invoiceNumber}</div>
                <DoubleScrollbar>
                  <table className="min-w-full border border-[var(--color-border)]">
                    <thead className="bg-[var(--color-background)] text-[var(--color-text-secondary)]">
                      <tr>
                        <th className="px-2 py-1 border border-[var(--color-border)]">Select</th>
                        <th className="px-2 py-1 border border-[var(--color-border)]">Product</th>
                        <th className="px-2 py-1 border border-[var(--color-border)]">Unit</th>
                        <th className="px-2 py-1 border border-[var(--color-border)]">Max Qty</th>
                        <th className="px-2 py-1 border border-[var(--color-border)]">Rate</th>
                        <th className="px-2 py-1 border border-[var(--color-border)]">Discount</th>
                        <th className="px-2 py-1 border border-[var(--color-border)]">Amount</th>
                        <th className="px-2 py-1 border border-[var(--color-border)]">Return Qty</th>
                        <th className="px-2 py-1 border border-[var(--color-border)]">Return Cause</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-border)]">
                      {returnProducts.map((p, idx) => {
                        // Find the original item from the delivery invoice to get rate, discount, and amount
                        const originalItem = selectedInvoice.items?.find(item => 
                          item.product?._id === p.productId || item.name === p.name
                        );
                        const rate = originalItem?.unitPrice || 0;
                        const discount = originalItem?.discount || 0;
                        const amount = originalItem?.totalPrice || 0;
                        
                        return (
                        <tr key={p.productId} className="hover:bg-[var(--color-background-hover)]">
                          <td className="px-2 py-1 border border-[var(--color-border)] text-center">
                            <input
                              type="checkbox"
                              checked={p.qty > 0}
                              onChange={e => handleReturnProductChange(idx, 'qty', e.target.checked ? 1 : 0)}
                            />
                          </td>
                          <td className="px-2 py-1 border border-[var(--color-border)]">{p.name}</td>
                          <td className="px-2 py-1 border border-[var(--color-border)]">{p.unit}</td>
                          <td className="px-2 py-1 border border-[var(--color-border)] text-center">{p.maxQty}</td>
                            <td className="px-2 py-1 border border-[var(--color-border)] text-right">৳{rate.toFixed(2)}</td>
                            <td className="px-2 py-1 border border-[var(--color-border)] text-right">৳{discount.toFixed(2)}</td>
                            <td className="px-2 py-1 border border-[var(--color-border)] text-right">৳{amount.toFixed(2)}</td>
                          <td className="px-2 py-1 border border-[var(--color-border)] text-center">
                            <Input
                              type="number"
                              min={0}
                              max={p.maxQty}
                              value={p.qty}
                              onChange={e => handleReturnProductChange(idx, 'qty', Math.max(0, Math.min(p.maxQty, Number(e.target.value))))}
                              className="w-20 text-center"
                              disabled={p.maxQty === 0}
                            />
                          </td>
                          <td className="px-2 py-1 border border-[var(--color-border)]">
                            <select
                              value={p.cause}
                              onChange={e => handleReturnProductChange(idx, 'cause', e.target.value)}
                              className="w-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded p-1 text-sm"
                              disabled={p.qty === 0}
                            >
                              <option value="">Select Cause</option>
                              {RETURN_CAUSES.map(cause => (
                                <option key={cause} value={cause}>{cause}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </DoubleScrollbar>
                <div className="flex justify-end gap-3 pt-4 border-t border-[var(--color-border)]">
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    onClick={handleSubmitReturn}
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Return'}
                  </button>
                </div>
              </div>
            )}
          </Modal>

          {/* Return Invoices List */}
          <div>
            <h2 className="text-lg font-semibold mb-4 text-[var(--color-text)]">Return Invoices</h2>
            <DoubleScrollbar className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow">
              <table className="min-w-full divide-y divide-[var(--color-border)]">
                <thead className="bg-[var(--color-background)]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Return #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">From Delivery</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Delivery Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Project</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Work Order No.</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Work Order Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Products</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)] text-[var(--color-text)]">
                  {returnInvoices.length === 0 && (
                    <tr><td colSpan={10} className="text-center py-8 text-[var(--color-text-secondary)]">No return invoices found.</td></tr>
                  )}
                  {returnInvoices.map(inv => {
                    let delivery: DeliveryInvoice | undefined = undefined;
                    if (typeof inv.deliveryInvoiceId === 'object' && inv.deliveryInvoiceId !== null) {
                      delivery = inv.deliveryInvoiceId as DeliveryInvoice;
                    } else if (typeof inv.deliveryInvoiceId === 'string') {
                      delivery = deliveryInvoices.find(d => d._id === inv.deliveryInvoiceId);
                    }
                    let projectName = '-';
                    if (delivery) {
                      const projectField = delivery.project;
                      if (typeof projectField === 'object' && projectField?.name) {
                        projectName = projectField.name;
                      } else if (delivery && delivery.project && typeof delivery.project === 'string' && projects.length > 0) {
                        const deliveryProjectId = delivery.project;
                        projectName = projects.find(p => String(p._id) === String(deliveryProjectId))?.name || '-';
                      }
                    }
                    return (
                    <tr key={inv._id} className="hover:bg-[var(--color-background-hover)] transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">{inv.invoiceNumber || inv.returnNumber || inv._id}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{delivery?.invoiceNumber || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{delivery?.createdAt ? new Date(delivery.createdAt).toLocaleDateString() : '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{projectName}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{delivery?.workOrder?.number || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{delivery?.workOrder?.date ? new Date(delivery.workOrder.date).toLocaleDateString() : '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{inv.customer?.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{new Date(inv.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(inv.items || []).map(item => (
                          <div key={item.productId}>
                            {item.name} - {item.quantity} ({item.remarks})
                          </div>
                        ))}
                      </td>
                        <td className="px-6 py-4 whitespace-nowrap flex gap-2">
                          <button title="View" onClick={() => handleView(inv)} className="text-blue-600 dark:text-blue-400 hover:opacity-80"><Eye size={18} /></button>
                          <button title="Edit" onClick={() => handleEdit(inv)} className="text-amber-600 dark:text-amber-400 hover:opacity-80"><Edit size={18} /></button>
                          <button title="Print" onClick={() => handlePrint(inv)} className="text-green-600 dark:text-green-400 hover:opacity-80"><Printer size={18} /></button>
                          <button title="Delete" onClick={() => handleDelete(inv._id)} className="text-red-600 dark:text-red-400 hover:opacity-80"><Trash2 size={18} /></button>
                        </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </DoubleScrollbar>
          </div>

          {/* View Modal */}
          <Modal isOpen={viewModalOpen} onClose={() => setViewModalOpen(false)} title="Return Invoice Details" size="xl" className="!max-w-7xl">
            {activeInvoice && (() => {
              // Get delivery invoice object if populated
              const delivery = typeof activeInvoice.deliveryInvoiceId === 'object' && activeInvoice.deliveryInvoiceId !== null ? activeInvoice.deliveryInvoiceId : undefined;
              // Project name: try populated, then lookup from projects array
              let projectName = '-';
              if (delivery) {
                if (delivery.project && typeof delivery.project === 'object' && 'name' in delivery.project && delivery.project.name) {
                  projectName = delivery.project.name;
                } else if (delivery && delivery.project && typeof delivery.project === 'string' && projects.length > 0) {
                  const deliveryProjectId = delivery.project;
                  projectName = projects.find(p => String(p._id) === String(deliveryProjectId))?.name || '-';
                }
              }
              // Helper to get product details from delivery invoice's items
              const getProductDetails = (productId: string) => {
                if (delivery && Array.isArray(delivery.items)) {
                  const found = delivery.items.find((item: any) => {
                    if (item.product && typeof item.product === 'object') {
                      return item.product._id === productId;
                    }
                    return false;
                  });
                  if (found && found.product && typeof found.product === 'object') {
                    return found.product;
                  }
                }
                return null;
              };
              return (
                <div className="space-y-2">
                  <div><strong>Return #:</strong> {activeInvoice.invoiceNumber}</div>
                  <div><strong>Customer:</strong> {activeInvoice.customer?.name}</div>
                  <div><strong>Date:</strong> {new Date(activeInvoice.createdAt).toLocaleDateString()}</div>
                  <div>
                    <strong>From Delivery:</strong> {delivery?.invoiceNumber || '-'}</div>
                  <div>
                    <strong>Delivery Date:</strong> {delivery?.createdAt ? new Date(delivery.createdAt).toLocaleDateString() : '-'}</div>
                  <div>
                    <strong>Project:</strong> {projectName}</div>
                  <div>
                    <strong>Work Order No.:</strong> {delivery?.workOrder?.number || '-'}</div>
                  <div>
                    <strong>Work Order Date:</strong> {delivery?.workOrder?.date ? new Date(delivery.workOrder.date).toLocaleDateString() : '-'}</div>
                  <div>
                    <strong>Items:</strong>
                    <ul className="list-disc ml-6">
                      {activeInvoice.items.map((item, idx) => {
                        const product = getProductDetails(item.productId);
                        return (
                          <li key={idx}>
                            {product ? (
                              <>
                                <span className="font-semibold">{product.name}</span>
                                {product.unit ? ` (${product.unit})` : ''}
                                {'description' in product && product.description ? ` - ${product.description}` : ''}
                              </>
                            ) : item.name || '-'}
                            {` - Qty: ${item.quantity ?? '-'}`}
                            {` - Cause: ${item.remarks ?? '-'}`}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              );
            })()}
          </Modal>
          {/* Edit Modal */}
          <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} title="Edit Return Invoice" size="lg">
            {activeInvoice && (
              <div className="space-y-4 text-[var(--color-text)]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Return Number</label>
                    <Input
                      value={activeInvoice.invoiceNumber || activeInvoice.returnNumber || ''}
                      readOnly
                      className="bg-[var(--color-background)] opacity-80"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Customer</label>
                    <Input
                      value={activeInvoice.customer?.name || ''}
                      readOnly
                      className="bg-[var(--color-background)] opacity-80"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Date</label>
                    <Input
                      value={new Date(activeInvoice.createdAt).toLocaleDateString()}
                      readOnly
                      className="bg-[var(--color-background)] opacity-80"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Status</label>
                    <select className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="processing">Processing</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                {/* Return Items */}
              <div>
                  <h3 className="text-lg font-semibold mb-3">Return Items</h3>
                  <DoubleScrollbar>
                    <table className="min-w-full border border-[var(--color-border)]">
                      <thead className="bg-[var(--color-background)] text-[var(--color-text-secondary)]">
                        <tr>
                          <th className="px-3 py-2 border border-[var(--color-border)] text-left">Product</th>
                          <th className="px-3 py-2 border border-[var(--color-border)] text-left">Unit</th>
                          <th className="px-3 py-2 border border-[var(--color-border)] text-right">Quantity</th>
                          <th className="px-3 py-2 border border-[var(--color-border)] text-left">Return Cause</th>
                          <th className="px-3 py-2 border border-[var(--color-border)] text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--color-border)]">
                        {activeInvoice.items.map((item, idx) => (
                          <tr key={idx} className="hover:bg-[var(--color-background-hover)]">
                            <td className="px-3 py-2 border border-[var(--color-border)]">{item.name}</td>
                            <td className="px-3 py-2 border border-[var(--color-border)]">
                              {item.unit || '-'}
                            </td>
                            <td className="px-3 py-2 border border-[var(--color-border)] text-right">
                              <Input
                                type="number"
                                min="0"
                                value={item.quantity}
                                onChange={(e) => {
                                  // Handle quantity change
                                  const newItems = [...activeInvoice.items];
                                  newItems[idx] = { ...item, quantity: parseInt(e.target.value) || 0 };
                                  setActiveInvoice({ ...activeInvoice, items: newItems });
                                }}
                                className="w-20 text-center"
                              />
                            </td>
                            <td className="px-3 py-2 border border-[var(--color-border)]">
                              <select
                                value={item.remarks}
                                onChange={(e) => {
                                  // Handle cause change
                                  const newItems = [...activeInvoice.items];
                                  newItems[idx] = { ...item, remarks: e.target.value };
                                  setActiveInvoice({ ...activeInvoice, items: newItems });
                                }}
                                className="w-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded p-1"
                              >
                                <option value="">Select Cause</option>
                                {RETURN_CAUSES.map(cause => (
                                  <option key={cause} value={cause}>{cause}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2 border border-[var(--color-border)] text-center">
                              <button
                                onClick={() => {
                                  // Remove item
                                  const newItems = activeInvoice.items.filter((_, i) => i !== idx);
                                  setActiveInvoice({ ...activeInvoice, items: newItems });
                                }}
                                className="text-red-600 dark:text-red-400 hover:opacity-80"
                                title="Remove item"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DoubleScrollbar>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-[var(--color-border)]">
                  <button
                    onClick={() => setEditModalOpen(false)}
                    className="px-4 py-2 border border-[var(--color-border)] text-[var(--color-text-secondary)] rounded-lg hover:bg-[var(--color-surface-hover)]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        // Update return invoice
                        await apiService.put(`/invoices/${activeInvoice._id}`, {
                          ...activeInvoice,
                          // Only send necessary fields for update
                          items: activeInvoice.items.map(item => ({
                            productId: item.productId,
                            name: item.name,
                            quantity: item.quantity,
                            remarks: item.remarks
                          }))
                        });
                        toast.success('Return invoice updated successfully');
                        setEditModalOpen(false);
                        fetchAll(); // Refresh the list
                      } catch (error) {
                        toast.error('Failed to update return invoice');
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            )}
          </Modal>
          {/* Print Modal */}
          <PrintModal 
            isOpen={printModalOpen} 
            onClose={() => setPrintModalOpen(false)} 
            title="Print Return Invoice"
          >
            {activeInvoice && (() => {
              // Get delivery invoice object if populated
              const delivery = typeof activeInvoice.deliveryInvoiceId === 'object' && activeInvoice.deliveryInvoiceId !== null ? activeInvoice.deliveryInvoiceId : undefined;
              // Project name: try populated, then lookup from projects array
              let projectName = '-';
              if (delivery) {
                if (delivery.project && typeof delivery.project === 'object' && 'name' in delivery.project && delivery.project.name) {
                  projectName = delivery.project.name;
                } else if (delivery && delivery.project && typeof delivery.project === 'string' && projects.length > 0) {
                  const deliveryProjectId = delivery.project;
                  projectName = projects.find(p => String(p._id) === String(deliveryProjectId))?.name || '-';
                }
              }
              const company = (settings?.company || {}) as any;
              const returnSettings = (settings?.returnInvoice || {}) as any;
              const logoUrl = 'file:///Volumes/Ext%20SSD/GDrive/Program/aircons-inventory-system/client/build/The%20Aircons%20Ltd.%20Logo%20Org.png';
              // Define styles similar to delivery invoice
              const invoiceHeaderRowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: 0, width: 'auto', padding: 0 } as any;
              const logoContainerStyle: React.CSSProperties = { flex: '0 0 auto', paddingLeft: 48, paddingTop: 32 } as any;
              const companyInfoStyle: React.CSSProperties = { flex: '1 1 auto', textAlign: 'right', marginLeft: 16, paddingRight: 48, paddingTop: 32 } as any;
              const companyInfoTextStyle: React.CSSProperties = { fontSize: '9.75pt' } as any;
              const invoiceTitleStyle: React.CSSProperties = { textAlign: 'center', fontWeight: 'bold', marginBottom: 4, fontSize: '14pt' } as any;
              const invoiceDetailsRowStyle: React.CSSProperties = { display: 'flex', border: '1px solid #000', borderBottom: 'none' } as any;
              const customerDetailsColStyle: React.CSSProperties = { flex: 1, borderRight: '1px solid #000', padding: '8px' } as any;
              const deliveryDetailsColStyle: React.CSSProperties = { flex: 1, padding: '8px' } as any;
              const customerInfoTitleStyle: React.CSSProperties = { fontWeight: 'bold', borderBottom: '1px solid #000', marginBottom: 4, fontSize: '11.5pt' } as any;
              const deliveryInfoTitleStyle: React.CSSProperties = { fontWeight: 'bold', borderBottom: '1px solid #000', marginBottom: 4, fontSize: '11.5pt' } as any;
              const deliveryInfoRowStyle: React.CSSProperties = { display: 'flex' } as any;
              const deliveryInfoLabelStyle: React.CSSProperties = { flex: 1, fontWeight: 'bold', fontSize: '11.5pt' } as any;
              const deliveryInfoValueStyle: React.CSSProperties = { flex: 1, fontSize: '11.5pt' } as any;
              const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginBottom: 0 } as any;
              const thSLStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', width: '32px', textAlign: 'center', fontSize: '11.5pt' } as any;
              const thDescStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', textAlign: 'left' } as any;
              const thQtyStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', width: '60px', textAlign: 'center', fontSize: '11.5pt' } as any;
              const thUnitStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', width: '60px', textAlign: 'center', fontSize: '11.5pt' } as any;
              const thCauseStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', width: '80px', textAlign: 'center', fontSize: '11.5pt' } as any;
              const trStyle: React.CSSProperties = { verticalAlign: 'top' } as any;
              const tdSLStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11.5pt' } as any;
              const tdDescStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', verticalAlign: 'top', whiteSpace: 'pre-line', fontSize: '11pt' } as any;
              const tdQtyStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11.5pt' } as any;
              const tdUnitStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11.5pt' } as any;
              const tdCauseStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11pt' } as any;
              const bottomSectionStyle: React.CSSProperties = { pageBreakInside: 'avoid', breakInside: 'avoid' } as any;
              const bottomTableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginTop: 0, background: '#fff' } as any;
              const bottomTdLeftStyle: React.CSSProperties = { verticalAlign: 'top', textAlign: 'left', fontSize: '10.5pt', width: '40%', padding: '0 6pt' } as any;
              const bottomTdRightStyle: React.CSSProperties = { verticalAlign: 'top', textAlign: 'right', fontSize: '10.5pt', width: '40%', padding: '0 6pt' } as any;
              const bottomTrStyle: React.CSSProperties = { minHeight: 32 } as any;
              const bottomTdSignStyle: React.CSSProperties = { fontWeight: 'bold', fontSize: '10.875pt', padding: '0 6pt 6pt 6pt', verticalAlign: 'bottom' } as any;
              const bottomTdSignRightStyle: React.CSSProperties = { fontWeight: 'bold', fontSize: '10.875pt', padding: '0 6pt 6pt 6pt', textAlign: 'right', verticalAlign: 'bottom' } as any;
              const bottomFooterStyle: React.CSSProperties = { fontSize: '9pt', color: '#222', marginTop: 8, display: 'flex', justifyContent: 'space-between', width: '100%' } as any;
              const printTimeStyle: React.CSSProperties = { textAlign: 'right' } as any;
              const customerNameStyle: React.CSSProperties = { fontWeight: 'bold', fontSize: '11.5pt' } as any;
              const customerAddressStyle: React.CSSProperties = { fontSize: '11.5pt' } as any;
              const projectInfoStyle: React.CSSProperties = { fontWeight: 'bold', borderTop: '1px solid #000', borderBottom: '1px solid #000', margin: '16px 0 4px 0', fontSize: '11.5pt', paddingTop: 4 } as any;
              const projectNameStyle: React.CSSProperties = { fontWeight: 'bold', fontSize: '11.5pt' } as any;

              return (
                <div
                  className="invoice-container"
                  style={{
                    margin: '24px auto',
                    maxWidth: '210mm',
                    background: '#fff',
                    padding: 0,
                    fontFamily: 'Times New Roman, serif',
                    fontSize: '9pt',
                    color: '#000',
                  } as any}
                >
                  <style>{`
                    @page { margin: 0; }
                    .invoice-row { display: none; }
                    @media screen { .invoice-row.visible { display: table-row; } }
                    @media print { .invoice-row { display: table-row !important; } .print:hidden { display: none !important; } }
                    .invoice-bottom-section { page-break-inside: avoid; break-inside: avoid; }
                    .print-invoice-area, .print-invoice-area * {
                      font-size: 9pt !important;
                    }
                    .invoice-title {
                      font-size: 14pt !important;
                      font-weight: bold !important;
                    }
                    @media print {
                      @page { size: A4 portrait; margin: 10mm; }
                      body, html {
                        width: 210mm;
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                      }
                      .print-invoice-area, .print-invoice-area * {
                        font-size: 9pt !important;
                      }
                      .invoice-title {
                        font-size: 14pt !important;
                        font-weight: bold !important;
                      }
                    }
                  `}</style>
                  
                  <div className="bg-white print-invoice-area print-active" style={{
                    fontFamily: 'Times New Roman, serif',
                    fontSize: '9pt',
                    color: '#000',
                    boxSizing: 'border-box',
                    margin: 0,
                    padding: 0,
                    position: 'relative',
                    background: '#fff',
                    borderRadius: 0,
                    boxShadow: 'none',
                    overflow: 'visible',
                    paddingBottom: 0,
                  } as any}>
                  {/* Header */}
                    <div className="invoice-header-row" style={invoiceHeaderRowStyle}>
                      <div style={logoContainerStyle}>
                        <img
                          src={logoUrl}
                          alt="Invoice Logo"
                          height={40}
                          style={{ height: 40, width: 'auto', display: 'block' } as any}
                          className="invoice-logo"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                    </div>
                      <div className="company-info" style={companyInfoStyle}>
                        <div style={companyInfoTextStyle}>Plot-14, Block-C, Main Road, Banasree, Rampura, Dhaka-1219.</div>
                        <div style={companyInfoTextStyle}>Phone: +880-1749-121607 | Email: support@theaircons.com</div>
                        <div style={companyInfoTextStyle}>Website: https://www.theaircons.com</div>
                      </div>
                    </div>
                    
                    <div style={{ padding: '0 40px', margin: 0 } as any}>
                      <div className="invoice-title" style={invoiceTitleStyle}>Return Invoice</div>
                      
                      {/* Invoice Details */}
                      <div style={invoiceDetailsRowStyle}>
                        <div style={customerDetailsColStyle}>
                          <div style={customerInfoTitleStyle}>Customer Information</div>
                          <div style={customerNameStyle}>{activeInvoice.customer?.name || 'Customer Name'}</div>
                          <div style={projectInfoStyle}>Project Information</div>
                          <div style={projectNameStyle}>{projectName}</div>
                        </div>
                        <div style={deliveryDetailsColStyle}>
                          <div style={deliveryInfoTitleStyle}>Return Information</div>
                          <div style={deliveryInfoRowStyle}>
                            <div style={deliveryInfoLabelStyle}>Return Invoice No.</div>
                            <div style={deliveryInfoValueStyle}>{activeInvoice.invoiceNumber || activeInvoice.returnNumber || activeInvoice._id}</div>
                          </div>
                          <div style={deliveryInfoRowStyle}>
                            <div style={deliveryInfoLabelStyle}>Date</div>
                            <div style={deliveryInfoValueStyle}>{new Date(activeInvoice.createdAt).toLocaleDateString()}</div>
                  </div>
                          <div style={deliveryInfoRowStyle}>
                            <div style={deliveryInfoLabelStyle}>From Delivery</div>
                            <div style={deliveryInfoValueStyle}>{delivery?.invoiceNumber || '-'}</div>
                    </div>
                          <div style={deliveryInfoRowStyle}>
                            <div style={deliveryInfoLabelStyle}>Delivery Date</div>
                            <div style={deliveryInfoValueStyle}>{delivery?.createdAt ? new Date(delivery.createdAt).toLocaleDateString() : '-'}</div>
                    </div>
                          <div style={deliveryInfoRowStyle}>
                            <div style={deliveryInfoLabelStyle}>Work Order No.</div>
                            <div style={deliveryInfoValueStyle}>{delivery?.workOrder?.number || '-'}</div>
                    </div>
                    </div>
                  </div>
                      
                  {/* Items Table */}
                      <table style={tableStyle}>
                        <thead>
                          <tr style={trStyle}>
                            <th style={thSLStyle}>#</th>
                            <th style={thDescStyle}>Product Description</th>
                            <th style={thUnitStyle}>Unit</th>
                            <th style={thQtyStyle}>Qty</th>
                            <th style={thCauseStyle}>Return Cause</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeInvoice.items.length === 0 && (
                            <tr style={trStyle}>
                              <td colSpan={5} style={{ border: '1px solid #000', padding: '3pt', textAlign: 'center', fontSize: '11.5pt' }}>
                                No items.
                              </td>
                            </tr>
                        )}
                        {activeInvoice.items.map((item, idx) => (
                            <tr key={idx} style={trStyle}>
                              <td style={tdSLStyle}>{idx + 1}</td>
                              <td style={tdDescStyle}>{item.name}</td>
                              <td style={tdUnitStyle}>{item.unit || '-'}</td>
                              <td style={tdQtyStyle}>{item.quantity}</td>
                              <td style={tdCauseStyle}>{item.remarks || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                      
                      {/* Bottom Section */}
                      <div className="invoice-bottom-section" style={bottomSectionStyle}>
                        <table style={bottomTableStyle}>
                          <tbody>
                            <tr style={bottomTrStyle}>
                              <td style={bottomTdLeftStyle}>
                                <div style={bottomTdSignStyle}>Customer Signature</div>
                              </td>
                              <td style={bottomTdRightStyle}>
                                <div style={bottomTdSignRightStyle}>Authorised Signatory</div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <div style={bottomFooterStyle}>
                          <span>This is a computer-generated document.</span>
                          <span style={printTimeStyle}>Print Time: {new Date().toLocaleString()}</span>
                        </div>
                  </div>
                  </div>
                  </div>
                </div>
              );
            })()}
          </PrintModal>
        </>
      )}
    </div>
  );
};

export default ReturnInvoicesPage; 